import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  TextInput,
  Platform,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  NativeStackNavigationProp,
  NativeStackScreenProps,
} from '@react-navigation/native-stack';
import { MaterialIcons, Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import type { RootStackParamList } from '../navigation/stackTypes';
import { isSuccessful } from '../services/api-requests';
import Header from '../components/Header';
import LoadingButton from '../components/LoadingButton';
import {
  getPricingCodeDetail,
  revokePricingCode,
  revokeCodeRedeemer,
  restoreCodeRedeemer,
  searchCodeRedeemers,
} from '../services/pricing-code-service';
import type {
  PricingCode,
  PricingCodeRedeemer,
} from '../models/pricing-code-model';
import type { DiscountType } from '../models/pricing-group-model';
import {
  formatDiscountLabel,
  DISCOUNT_BADGE_COLORS,
} from '../models/pricing-group-model';
import { showThemedAlert } from '../components/common/ThemedAlert';

type TabType = 'details' | 'redeemers' | 'chargers';

type PricingCodeDetailNavigationProp = NativeStackNavigationProp<
  RootStackParamList,
  'PricingCodeDetail'
>;

const TAB_CONFIG: { key: TabType; label: string }[] = [
  { key: 'details', label: 'Details' },
  { key: 'redeemers', label: 'Redeemers' },
  { key: 'chargers', label: 'Chargers' },
];

// ─── Helper components ───────────────────────────────────────────────

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <View style={styles.infoRow}>
    <Text style={styles.infoLabel}>{label}</Text>
    <Text style={styles.infoValue}>{value}</Text>
  </View>
);

const StatusBadge = ({
  isActive,
  label,
}: {
  isActive: boolean;
  label?: string;
}) => (
  <View
    style={[
      styles.statusBadge,
      { backgroundColor: isActive ? '#10B98133' : '#EF444433' },
    ]}
  >
    <View
      style={[
        styles.statusDot,
        { backgroundColor: isActive ? '#10B981' : '#EF4444' },
      ]}
    />
    <Text
      style={[
        styles.statusBadgeText,
        { color: isActive ? '#10B981' : '#EF4444' },
      ]}
    >
      {label ?? (isActive ? 'Active' : 'Revoked')}
    </Text>
  </View>
);

const DiscountBadge = ({
  type,
  value,
}: {
  type: DiscountType;
  value: number;
}) => (
  <View
    style={[
      styles.discountBadge,
      { backgroundColor: DISCOUNT_BADGE_COLORS[type] + '22' },
    ]}
  >
    <Text
      style={[styles.discountBadgeText, { color: DISCOUNT_BADGE_COLORS[type] }]}
    >
      {formatDiscountLabel(type, value)}
    </Text>
  </View>
);

// ─── Main component ──────────────────────────────────────────────────

const PricingCodeDetail = () => {
  const navigation = useNavigation<PricingCodeDetailNavigationProp>();
  const route =
    useRoute<NativeStackScreenProps<RootStackParamList, 'PricingCodeDetail'>['route']>();
  const { codeId } = route.params;

  const [code, setCode] = useState<PricingCode | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('details');

  // Redeemers state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PricingCodeRedeemer[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Action loading states
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [redeemerActionLoading, setRedeemerActionLoading] = useState<string | null>(null);

  // ── Data fetching ────────────────────────────────────────────────

  const fetchCodeDetail = useCallback(async () => {
    try {
      const response = await getPricingCodeDetail(codeId);
      if (isSuccessful(response.status) && response.payload) {
        setCode(response.payload as PricingCode);
      } else {
        showThemedAlert(
          'Error',
          'Failed to load pricing code details.',
          [{ text: 'OK', onPress: () => navigation.goBack() }],
        );
      }
    } catch {
      showThemedAlert(
        'Error',
        'An unexpected error occurred.',
        [{ text: 'OK', onPress: () => navigation.goBack() }],
      );
    }
  }, [codeId, navigation]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchCodeDetail();
      setLoading(false);
    };
    load();
  }, [fetchCodeDetail]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchCodeDetail();
    setSearchResults(null);
    setSearchQuery('');
    setRefreshing(false);
  }, [fetchCodeDetail]);

  // ── Copy code ────────────────────────────────────────────────────

  const copyCode = async () => {
    if (!code) return;
    await Clipboard.setStringAsync(code.code);
    showThemedAlert(
      'Copied!',
      'Code copied to clipboard.',
      [{ text: 'OK' }],
    );
  };

  // ── Revoke code ─────────────────────────────────────────────────

  const handleRevokeCode = () => {
    if (!code) return;

    showThemedAlert(
      'Revoke Code',
      `Are you sure you want to revoke "${code.name}"? All redeemers will lose their discount.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Revoke',
          style: 'destructive',
          onPress: async () => {
            setRevokeLoading(true);
            try {
              const response = await revokePricingCode(codeId);
              if (isSuccessful(response.status)) {
                showThemedAlert('Done', 'Pricing code has been revoked.', [
                  { text: 'OK', onPress: () => navigation.goBack() },
                ]);
              } else {
                showThemedAlert('Error', 'Failed to revoke pricing code.', [{ text: 'OK' }]);
              }
            } catch {
              showThemedAlert('Error', 'An unexpected error occurred.', [{ text: 'OK' }]);
            } finally {
              setRevokeLoading(false);
            }
          },
        },
      ],
    );
  };

  // ── Redeemer search ──────────────────────────────────────────────

  const handleSearch = useCallback(
    (text: string) => {
      setSearchQuery(text);

      if (searchTimeout) clearTimeout(searchTimeout);

      if (!text.trim()) {
        setSearchResults(null);
        setSearching(false);
        return;
      }

      const timeout = setTimeout(async () => {
        setSearching(true);
        try {
          const response = await searchCodeRedeemers(codeId, text.trim());
          if (isSuccessful(response.status) && response.payload) {
            setSearchResults(response.payload.results);
          }
        } catch {
          // Silently fail search, user can retry
        } finally {
          setSearching(false);
        }
      }, 400);

      setSearchTimeout(timeout);
    },
    [codeId, searchTimeout],
  );

  // ── Redeemer revoke / restore ────────────────────────────────────

  const handleRevokeRedeemer = (redeemer: PricingCodeRedeemer) => {
    const displayName = redeemer.user_email || redeemer.username || 'this redeemer';

    showThemedAlert(
      'Revoke Redeemer',
      `Are you sure you want to revoke access for ${displayName}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Revoke',
          style: 'destructive',
          onPress: async () => {
            setRedeemerActionLoading(redeemer.user_id);
            try {
              const response = await revokeCodeRedeemer(codeId, redeemer.user_id);
              if (isSuccessful(response.status)) {
                await fetchCodeDetail();
                showThemedAlert('Done', `Access revoked for ${displayName}.`, [{ text: 'OK' }]);
              } else {
                showThemedAlert('Error', 'Failed to revoke redeemer.', [{ text: 'OK' }]);
              }
            } catch {
              showThemedAlert('Error', 'An unexpected error occurred.', [{ text: 'OK' }]);
            } finally {
              setRedeemerActionLoading(null);
            }
          },
        },
      ],
    );
  };

  const handleRestoreRedeemer = async (redeemer: PricingCodeRedeemer) => {
    const displayName = redeemer.user_email || redeemer.username || 'this redeemer';

    setRedeemerActionLoading(redeemer.user_id);
    try {
      const response = await restoreCodeRedeemer(codeId, redeemer.user_id);
      if (isSuccessful(response.status)) {
        await fetchCodeDetail();
        showThemedAlert('Done', `Access restored for ${displayName}.`, [{ text: 'OK' }]);
      } else {
        showThemedAlert('Error', 'Failed to restore redeemer.', [{ text: 'OK' }]);
      }
    } catch {
      showThemedAlert('Error', 'An unexpected error occurred.', [{ text: 'OK' }]);
    } finally {
      setRedeemerActionLoading(null);
    }
  };

  // ── Date formatting ──────────────────────────────────────────────

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string): string => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // ── Scope display ────────────────────────────────────────────────

  const getScopeLabel = (scope: string): string => {
    switch (scope) {
      case 'all_host_chargers':
        return 'All Chargers';
      case 'specific_chargers':
        return 'Specific Chargers';
      default:
        return scope;
    }
  };

  // ── Tab rendering ────────────────────────────────────────────────

  const renderTabBar = () => (
    <View style={styles.tabBar}>
      {TAB_CONFIG.map((tab) => {
        const isActive = activeTab === tab.key;
        let countBadge: number | null = null;
        if (tab.key === 'redeemers') countBadge = code?.redeemers?.length ?? 0;
        if (tab.key === 'chargers') countBadge = code?.chargers?.length ?? 0;

        return (
          <Pressable
            key={tab.key}
            style={[styles.tab, isActive && styles.activeTab]}
            onPress={() => setActiveTab(tab.key)}
          >
            <View style={styles.tabLabelRow}>
              <Text style={[styles.tabText, isActive && styles.activeTabText]}>
                {tab.label}
              </Text>
              {countBadge !== null && countBadge > 0 && (
                <View style={styles.tabCountBadge}>
                  <Text style={styles.tabCountText}>{countBadge}</Text>
                </View>
              )}
            </View>
          </Pressable>
        );
      })}
    </View>
  );

  // ── Details tab ──────────────────────────────────────────────────

  const renderDetailsTab = () => {
    if (!code) return null;

    const isExpired = code.expires_at ? new Date(code.expires_at) < new Date() : false;
    const redemptionLimit = code.max_redemptions
      ? `${code.total_redemptions} / ${code.max_redemptions}`
      : `${code.total_redemptions} (unlimited)`;

    return (
      <View style={styles.tabContent}>
        {/* Code display + copy */}
        <View style={styles.card}>
          <Text style={styles.cardSectionTitle}>Code</Text>
          <View style={styles.codeRow}>
            <View style={styles.codeContainer}>
              <Text style={styles.codeText}>{code.code}</Text>
            </View>
            <Pressable
              onPress={copyCode}
              style={({ pressed }) => [
                styles.copyButton,
                { opacity: pressed ? 0.6 : 1 },
              ]}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Ionicons name="copy-outline" size={20} color="#40BFB4" />
            </Pressable>
          </View>
        </View>

        {/* Status + Discount */}
        <View style={styles.card}>
          <View style={styles.cardHeaderRow}>
            <Text style={styles.cardTitle}>{code.name}</Text>
            <StatusBadge isActive={code.is_active} />
          </View>

          <View style={styles.discountRow}>
            <DiscountBadge type={code.discount_type} value={code.discount_value} />
            {isExpired && (
              <View style={[styles.statusBadge, { backgroundColor: '#F59E0B33' }]}>
                <Text style={[styles.statusBadgeText, { color: '#F59E0B' }]}>
                  Expired
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Info rows */}
        <View style={styles.card}>
          <Text style={styles.cardSectionTitle}>Details</Text>
          <InfoRow label="Redemptions" value={redemptionLimit} />
          <View style={styles.infoSeparator} />
          <InfoRow
            label="Expires"
            value={code.expires_at ? formatDate(code.expires_at) : 'No expiration'}
          />
          <View style={styles.infoSeparator} />
          <InfoRow label="Scope" value={getScopeLabel(code.scope)} />
          <View style={styles.infoSeparator} />
          <InfoRow label="Chargers" value={String(code.chargers?.length ?? 0)} />
          <View style={styles.infoSeparator} />
          <InfoRow label="Created" value={formatDate(code.created_at)} />
        </View>

        {/* Revoke button or revoked banner */}
        <View style={styles.actionSection}>
          {code.is_revoked ? (
            <View style={styles.revokedBanner}>
              <MaterialIcons name="block" size={18} color="#EF4444" />
              <Text style={styles.revokedBannerText}>
                Revoked on {formatDate(code.updated_at ?? code.created_at)}
              </Text>
            </View>
          ) : (
            <LoadingButton
              onPress={handleRevokeCode}
              backgroundColor="#EF4444"
              isLoading={revokeLoading}
              style={styles.fullWidthButton}
            >
              Revoke Code
            </LoadingButton>
          )}
        </View>
      </View>
    );
  };

  // ── Redeemers tab ────────────────────────────────────────────────

  const renderRedeemerRow = (
    redeemer: PricingCodeRedeemer,
    index: number,
  ) => {
    const displayName = redeemer.username || null;
    const isActionLoading = redeemerActionLoading === redeemer.user_id;
    const isActive = !redeemer.is_user_revoked;

    return (
      <View
        key={redeemer.id ?? `redeemer-${redeemer.user_id}-${index}`}
        style={styles.redeemerRow}
      >
        <View style={styles.redeemerInfo}>
          <View style={styles.redeemerAvatarContainer}>
            <Text style={styles.redeemerAvatarText}>
              {(redeemer.user_email?.[0] ?? '?').toUpperCase()}
            </Text>
          </View>
          <View style={styles.redeemerDetails}>
            <Text style={styles.redeemerEmail} numberOfLines={1}>
              {redeemer.user_email}
            </Text>
            {displayName && (
              <Text style={styles.redeemerName} numberOfLines={1}>
                {displayName}
              </Text>
            )}
            <View style={styles.redeemerMeta}>
              <StatusBadge isActive={isActive} />
              <Text style={styles.redeemerDate}>
                {formatDateTime(redeemer.redeemed_at)}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.redeemerActions}>
          {isActionLoading ? (
            <ActivityIndicator size="small" color="#40BFB4" />
          ) : isActive ? (
            <Pressable
              onPress={() => handleRevokeRedeemer(redeemer)}
              style={({ pressed }) => [
                styles.redeemerActionButton,
                styles.revokeButton,
                { opacity: pressed ? 0.6 : 1 },
              ]}
            >
              <MaterialIcons name="block" size={14} color="#EF4444" />
              <Text style={[styles.redeemerActionText, { color: '#EF4444' }]}>
                Revoke
              </Text>
            </Pressable>
          ) : (
            <Pressable
              onPress={() => handleRestoreRedeemer(redeemer)}
              style={({ pressed }) => [
                styles.redeemerActionButton,
                styles.restoreButton,
                { opacity: pressed ? 0.6 : 1 },
              ]}
            >
              <MaterialIcons name="restore" size={14} color="#10B981" />
              <Text style={[styles.redeemerActionText, { color: '#10B981' }]}>
                Restore
              </Text>
            </Pressable>
          )}
        </View>
      </View>
    );
  };

  const renderRedeemersTab = () => {
    if (!code) return null;

    const redeemers = searchResults ?? code.redeemers ?? [];
    const hasRedeemers = redeemers.length > 0;
    const activeCount = (code.redeemers ?? []).filter(
      (r) => !r.is_user_revoked,
    ).length;
    const revokedCount = (code.redeemers ?? []).length - activeCount;

    return (
      <View style={styles.tabContent}>
        {/* Stats row */}
        <View style={styles.statsRow}>
          <View style={styles.statCard}>
            <Text style={styles.statValue}>{code.redeemers?.length ?? 0}</Text>
            <Text style={styles.statLabel}>Total</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={[styles.statValue, { color: '#10B981' }]}>
              {activeCount}
            </Text>
            <Text style={styles.statLabel}>Active</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={[styles.statValue, { color: '#EF4444' }]}>
              {revokedCount}
            </Text>
            <Text style={styles.statLabel}>Revoked</Text>
          </View>
        </View>

        {/* Search bar */}
        <View style={styles.searchContainer}>
          <Ionicons
            name="search"
            size={18}
            color="#8D8E90"
            style={styles.searchIcon}
          />
          <TextInput
            style={styles.searchInput}
            placeholder="Search by email or name..."
            placeholderTextColor="#8D8E90"
            value={searchQuery}
            onChangeText={handleSearch}
            autoCapitalize="none"
            autoCorrect={false}
            returnKeyType="search"
          />
          {searchQuery.length > 0 && (
            <Pressable
              onPress={() => {
                setSearchQuery('');
                setSearchResults(null);
              }}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Ionicons name="close-circle" size={18} color="#8D8E90" />
            </Pressable>
          )}
          {searching && (
            <ActivityIndicator
              size="small"
              color="#40BFB4"
              style={{ marginLeft: 8 }}
            />
          )}
        </View>

        {searchResults !== null && (
          <Text style={styles.searchResultsCount}>
            {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
          </Text>
        )}

        {/* Redeemer list */}
        {hasRedeemers ? (
          <View style={styles.redeemerList}>
            {redeemers.map((redeemer, index) =>
              renderRedeemerRow(redeemer, index),
            )}
          </View>
        ) : (
          <View style={styles.emptyState}>
            <Ionicons name="people-outline" size={48} color="#8D8E90" />
            <Text style={styles.emptyTitle}>
              {searchQuery ? 'No results found' : 'No redeemers yet'}
            </Text>
            <Text style={styles.emptySubtitle}>
              {searchQuery
                ? 'Try a different search term.'
                : 'Share this code with drivers to get started.'}
            </Text>
          </View>
        )}
      </View>
    );
  };

  // ── Chargers tab ─────────────────────────────────────────────────

  const renderChargersTab = () => {
    if (!code) return null;

    if (code.scope === 'all_host_chargers') {
      return (
        <View style={styles.tabContent}>
          <View style={styles.allChargersCard}>
            <View style={styles.allChargersIconContainer}>
              <Ionicons name="flash" size={32} color="#40BFB4" />
            </View>
            <Text style={styles.allChargersTitle}>All Your Chargers</Text>
            <Text style={styles.allChargersSubtitle}>
              This pricing code applies to all your chargers. Any new chargers
              you add will also be covered automatically.
            </Text>
          </View>
        </View>
      );
    }

    const chargers = code.chargers ?? [];

    if (chargers.length === 0) {
      return (
        <View style={styles.tabContent}>
          <View style={styles.emptyState}>
            <Ionicons name="flash-outline" size={48} color="#8D8E90" />
            <Text style={styles.emptyTitle}>No chargers assigned</Text>
            <Text style={styles.emptySubtitle}>
              This code does not have any specific chargers assigned.
            </Text>
          </View>
        </View>
      );
    }

    return (
      <View style={styles.tabContent}>
        <Text style={styles.chargerCountHeader}>
          {chargers.length} charger{chargers.length !== 1 ? 's' : ''}
        </Text>
        {chargers.map((charger) => (
          <View key={charger.charger_id} style={styles.chargerRow}>
            <View style={styles.chargerIconContainer}>
              <Ionicons name="flash" size={20} color="#40BFB4" />
            </View>
            <View style={styles.chargerInfo}>
              <Text style={styles.chargerName} numberOfLines={1}>
                {charger.charger_name ?? charger.charger_id}
              </Text>
            </View>
          </View>
        ))}
      </View>
    );
  };

  // ── Loading state ────────────────────────────────────────────────

  if (loading) {
    return (
      <View style={styles.rootContainer}>
        <SafeAreaView style={styles.safeArea} edges={['top']}>
          <Header headerText="Pricing Code" />
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#40BFB4" />
            <Text style={styles.loadingText}>Loading code details...</Text>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  if (!code) {
    return (
      <View style={styles.rootContainer}>
        <SafeAreaView style={styles.safeArea} edges={['top']}>
          <Header headerText="Pricing Code" />
          <View style={styles.loadingContainer}>
            <Ionicons name="alert-circle-outline" size={48} color="#EF4444" />
            <Text style={styles.errorText}>Code not found</Text>
            <Pressable
              onPress={() => navigation.goBack()}
              style={styles.goBackButton}
            >
              <Text style={styles.goBackButtonText}>Go Back</Text>
            </Pressable>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  // ── Main render ──────────────────────────────────────────────────

  return (
    <View style={styles.rootContainer}>
      <SafeAreaView style={styles.safeArea} edges={['top']}>
        <Header headerText="Pricing Code" />

        {renderTabBar()}

        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor="#40BFB4"
              colors={['#40BFB4']}
            />
          }
        >
          {activeTab === 'details' && renderDetailsTab()}
          {activeTab === 'redeemers' && renderRedeemersTab()}
          {activeTab === 'chargers' && renderChargersTab()}
        </ScrollView>
      </SafeAreaView>
    </View>
  );
};

export default PricingCodeDetail;

// ─── Styles ──────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  rootContainer: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  safeArea: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  loadingText: {
    color: '#8D8E90',
    fontSize: 15,
    fontFamily: 'PlusJakartaSans-Regular',
    marginTop: 16,
  },
  errorText: {
    color: '#FFFFFF',
    fontSize: 17,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 24,
  },
  goBackButton: {
    backgroundColor: '#292D33',
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 24,
  },
  goBackButtonText: {
    color: '#40BFB4',
    fontSize: 15,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
  },

  // ── Tabs ─────────────────────────────────────────────────────────
  tabBar: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#292D33',
    paddingHorizontal: 16,
  },
  tab: {
    flex: 1,
    paddingVertical: 14,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomColor: '#40BFB4',
  },
  tabLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#8D8E90',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  activeTabText: {
    color: '#40BFB4',
  },
  tabCountBadge: {
    backgroundColor: '#40BFB422',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  tabCountText: {
    color: '#40BFB4',
    fontSize: 11,
    fontWeight: '700',
  },

  // ── Tab content ──────────────────────────────────────────────────
  tabContent: {
    paddingHorizontal: 16,
    paddingTop: 16,
  },

  // ── Cards ────────────────────────────────────────────────────────
  card: {
    backgroundColor: '#292D33',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
  },
  cardSectionTitle: {
    color: '#8D8E90',
    fontSize: 12,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 10,
  },
  cardHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  cardTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
    fontFamily: 'PlusJakartaSans-Regular',
    flex: 1,
    marginRight: 12,
  },

  // ── Code display ─────────────────────────────────────────────────
  codeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  codeContainer: {
    flex: 1,
    backgroundColor: '#1A1D21',
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#3A3F47',
  },
  codeText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    letterSpacing: 1.5,
  },
  copyButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#40BFB418',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // ── Discount + Status ────────────────────────────────────────────
  discountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  discountBadge: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 20,
  },
  discountBadgeText: {
    fontSize: 13,
    fontWeight: '700',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 20,
    gap: 5,
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
  },

  // ── Info rows ────────────────────────────────────────────────────
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  infoLabel: {
    color: '#8D8E90',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  infoValue: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  infoSeparator: {
    height: 1,
    backgroundColor: '#3A3F47',
  },

  // ── Action section ───────────────────────────────────────────────
  actionSection: {
    marginTop: 8,
    marginBottom: 16,
  },
  fullWidthButton: {
    borderRadius: 24,
  },
  revokedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#EF444418',
    borderRadius: 14,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#EF444430',
  },
  revokedBannerText: {
    color: '#EF4444',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
  },

  // ── Stats row ────────────────────────────────────────────────────
  statsRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 16,
  },
  statCard: {
    flex: 1,
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 14,
    alignItems: 'center',
  },
  statValue: {
    color: '#FFFFFF',
    fontSize: 22,
    fontWeight: '700',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  statLabel: {
    color: '#8D8E90',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    marginTop: 2,
  },

  // ── Search ───────────────────────────────────────────────────────
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#292D33',
    borderRadius: 12,
    paddingHorizontal: 14,
    marginBottom: 12,
    height: 46,
  },
  searchIcon: {
    marginRight: 10,
  },
  searchInput: {
    flex: 1,
    color: '#FFFFFF',
    fontSize: 15,
    fontFamily: 'PlusJakartaSans-Regular',
    paddingVertical: 0,
  },
  searchResultsCount: {
    color: '#8D8E90',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    marginBottom: 12,
  },

  // ── Redeemer list ────────────────────────────────────────────────
  redeemerList: {
    gap: 8,
  },
  redeemerRow: {
    backgroundColor: '#292D33',
    borderRadius: 14,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  redeemerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 10,
  },
  redeemerAvatarContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#3A3F47',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  redeemerAvatarText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
  redeemerDetails: {
    flex: 1,
  },
  redeemerEmail: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  redeemerName: {
    color: '#8D8E90',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    marginTop: 1,
  },
  redeemerMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
    gap: 8,
  },
  redeemerDate: {
    color: '#8D8E90',
    fontSize: 11,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  redeemerActions: {
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 80,
  },
  redeemerActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 20,
    gap: 5,
  },
  revokeButton: {
    backgroundColor: '#EF444418',
  },
  restoreButton: {
    backgroundColor: '#10B98118',
  },
  redeemerActionText: {
    fontSize: 12,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
  },

  // ── Chargers ─────────────────────────────────────────────────────
  chargerCountHeader: {
    color: '#8D8E90',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    marginBottom: 12,
  },
  chargerRow: {
    backgroundColor: '#292D33',
    borderRadius: 14,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  chargerIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#40BFB418',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  chargerInfo: {
    flex: 1,
  },
  chargerName: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  chargerSerial: {
    color: '#8D8E90',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    marginTop: 2,
  },

  // ── All chargers card ────────────────────────────────────────────
  allChargersCard: {
    backgroundColor: '#292D33',
    borderRadius: 16,
    padding: 28,
    alignItems: 'center',
  },
  allChargersIconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#40BFB418',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  allChargersTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
    fontFamily: 'PlusJakartaSans-Regular',
    marginBottom: 8,
  },
  allChargersSubtitle: {
    color: '#8D8E90',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    textAlign: 'center',
    lineHeight: 20,
  },

  // ── Empty state ──────────────────────────────────────────────────
  emptyState: {
    alignItems: 'center',
    paddingVertical: 48,
    paddingHorizontal: 32,
  },
  emptyTitle: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
    marginTop: 16,
    marginBottom: 6,
  },
  emptySubtitle: {
    color: '#8D8E90',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    textAlign: 'center',
    lineHeight: 20,
  },
});
